/**
 * Cache Manager - Handles app state cleanup and version-based cache invalidation
 * 
 * This module provides utilities to:
 * 1. Clear all app state when showing the login page
 * 2. Automatically clear cache when a new version is deployed
 * 3. Clean up localStorage, sessionStorage, cookies, browser cache, and Svelte stores
 * 
 * Note: User preferences (theme, etc.) are NOT preserved locally - they are
 * restored from the user's database profile after login.
 */

// Import stores that need to be reset
import { userStore } from '../stores/user-store.js';
import { resetChatStore, setChatUserContext } from '../stores/chat-store.js';
import { selectedObjects } from '../stores/selected-objects-store.js';
import { uploadStore } from '../stores/upload-store.js';
import { closeWizard } from '../stores/wizard-store.js';
import { drawerStore, isDrawerOpen } from '../stores/side-drawer-store.js';
import { drawerStore as objectDrawerStore } from '../stores/object-drawer-store.js';
import { lookupDrawerStore } from '../stores/lookup-drawer-store.js';
import { chatSessionStore } from '../stores/chat-session-store.js';
import { theme } from '../stores/theme-store.js';

// App version is injected at build time via rollup replace plugin
// Falls back to 'unknown' if not set (e.g., during development without proper build)
declare const __APP_VERSION__: string;
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

const VERSION_STORAGE_KEY = 'app_version';

/**
 * Keys that are preserved ONLY during version change cleanup (not login cleanup).
 * The version key is needed to track version changes.
 * All other keys are cleared - user preferences come from the database.
 */
const VERSION_PRESERVED_KEYS: string[] = [
  VERSION_STORAGE_KEY,
];

/**
 * Get the current app version
 */
export function getAppVersion(): string {
  return APP_VERSION;
}

/**
 * Check if the app version has changed since last load
 */
export function hasVersionChanged(): boolean {
  const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
  return storedVersion !== APP_VERSION;
}

/**
 * Update the stored version to current
 */
function updateStoredVersion(): void {
  try {
    localStorage.setItem(VERSION_STORAGE_KEY, APP_VERSION);
  } catch (_) {
    // Ignore storage errors
  }
}

/**
 * Log the current state of all caches/storage for debugging
 */
export function logCacheState(label: string = 'Current'): void {
  console.group(`%c[CacheManager] ${label} Cache State`, 'color: #2196F3; font-weight: bold');
  
  // localStorage
  try {
    const lsKeys: string[] = [];
    const lsData: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        lsKeys.push(key);
        const value = localStorage.getItem(key);
        if (value) {
          const preview = value.length > 150 ? value.substring(0, 150) + '...' : value;
          lsData[key] = preview;
        }
      }
    }
    if (Object.keys(lsData).length > 0) {
      console.table(lsData);
    }
  } catch (e) {
    logError(e, "CacheManager", undefined, { message: "localStorage access warning" });
  }
  
  // sessionStorage
  try {
    const ssKeys: string[] = [];
    const ssData: Record<string, string> = {};
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        ssKeys.push(key);
        const value = sessionStorage.getItem(key);
        if (value) {
          const preview = value.length > 150 ? value.substring(0, 150) + '...' : value;
          ssData[key] = preview;
        }
      }
    }
    if (Object.keys(ssData).length > 0) {
      console.table(ssData);
    }
  } catch (e) {
    logError(e, "CacheManager", undefined, { message: "sessionStorage access warning" });
  }
  
  // Cookies
  try {
    const cookies = document.cookie.split(';').filter(c => c.trim());
    const cookieData: Record<string, string> = {};
    cookies.forEach(cookie => {
      const [name, value] = cookie.split('=').map(s => s.trim());
      if (name) {
        cookieData[name] = value || '(no value)';
      }
    });
    if (Object.keys(cookieData).length > 0) {
      console.table(cookieData);
    }
  } catch (e) {
    logError(e, "CacheManager", undefined, { message: "cookies access warning" });
  }
  
  console.groupEnd();
}

/**
 * Clear all cookies for the current domain
 */
function clearAllCookies(): void {
  try {
    const cookies = document.cookie.split(';');
    const cookieNames = cookies.map(c => c.split('=')[0].trim()).filter(n => n);
    
    for (const cookie of cookies) {
      const name = cookie.split('=')[0].trim();
      if (name) {
        // Clear cookie for current path and all parent paths
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
      }
    }
  } catch (error) {
    logError(error, "CacheManager", undefined, { message: "Failed to clear cookies" });
  }
}

/**
 * Clear localStorage.
 * @param preserveVersionKey - If true, preserve only the version tracking key (for version change cleanup)
 */
function clearLocalStorage(preserveVersionKey: boolean = false): void {
  try {
    const keysToRemove: string[] = [];
    const keysToPreserve: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        if (preserveVersionKey && VERSION_PRESERVED_KEYS.includes(key)) {
          keysToPreserve.push(key);
          continue;
        }
        keysToRemove.push(key);
      }
    }
    
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
    
  } catch (error) {
    logError(error, "CacheManager", undefined, { message: "Failed to clear localStorage" });
  }
}

/**
 * Reset all Svelte stores to their initial state.
 * This ensures no stale data persists between user sessions.
 * Exported for use in other cleanup scenarios.
 */
export function resetAllStores(): void {
  console.group('[CacheManager] Resetting all Svelte stores');
  
  try {
    // User store
    userStore.clearUser();
    
    // Chat stores
    resetChatStore();
    setChatUserContext(null);
    chatSessionStore.clear();
    
    // Selection stores
    selectedObjects.clear();
    
    // Upload store
    uploadStore.reset();
    
    // UI state stores - reset to closed/initial state
    closeWizard();
    
    isDrawerOpen.set(false);
    drawerStore.set({ drawers: {}, activeDrawer: null });
    objectDrawerStore.set({
      instanceId: crypto.randomUUID(),
      open: false,
      objectType: null,
      action: null,
      id: null,
      object: null
    });
    lookupDrawerStore.set({
      instanceId: crypto.randomUUID(),
      open: false,
      section: null,
      items: [],
      lookupId: '',
      lookupState: ''
    });
    
    // Theme store - reset to system preference/default
    // User's theme preference will be restored from database after login
    theme.reset();
    
  } catch (error) {
    logError(error, "CacheManager", undefined, { message: "Failed to reset some stores" });
  }
  
  console.groupEnd();
}

/**
 * Clear all sessionStorage
 */
function clearSessionStorage(): void {
  try {
    const keyCount = sessionStorage.length;
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) keys.push(key);
    }
    
    sessionStorage.clear();
  } catch (error) {
    logError(error, "CacheManager", undefined, { message: "Failed to clear sessionStorage" });
  }
}

/**
 * Clear browser Cache API (if available)
 * This clears cached responses from fetch requests
 */
async function clearBrowserCache(): Promise<void> {
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
    }
  } catch (error) {
    logError(error, "CacheManager", undefined, { message: "Failed to clear browser cache" });
  }
}

/**
 * Unregister all service workers (if any exist)
 */
async function unregisterServiceWorkers(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map(registration => registration.unregister())
      );
      if (registrations.length > 0) {
      }
    }
  } catch (error) {
    logError(error, "CacheManager", undefined, { message: "Failed to unregister service workers" });
  }
}

/**
 * Clear IndexedDB databases (if any exist)
 * Note: This may not work in all browsers due to security restrictions
 */
async function clearIndexedDB(): Promise<void> {
  try {
    if ('indexedDB' in window && indexedDB.databases) {
      const databases = await indexedDB.databases();
      await Promise.all(
        databases.map(db => {
          if (db.name) {
            return new Promise<void>((resolve, reject) => {
              const request = indexedDB.deleteDatabase(db.name!);
              request.onsuccess = () => resolve();
              request.onerror = () => reject(request.error);
            });
          }
          return Promise.resolve();
        })
      );
    }
  } catch (error) {
    // This often fails due to browser restrictions, which is fine
    console.debug('[CacheManager] Could not clear IndexedDB (this is normal)');
  }
}

/**
 * Perform a complete cleanup of all app state.
 * This should be called when showing the login page to ensure a clean slate.
 * 
 * @param options Configuration options for cleanup
 * @returns Promise that resolves when cleanup is complete
 */
export async function performFullCleanup(options: {
  clearCookies?: boolean;
  clearLocalStorage?: boolean;
  clearSessionStorage?: boolean;
  clearBrowserCache?: boolean;
  clearServiceWorkers?: boolean;
  clearIndexedDB?: boolean;
  clearSvelteStores?: boolean;
  preserveVersionKey?: boolean;  // Only preserve version key (for version change cleanup)
} = {}): Promise<void> {
  const {
    clearCookies = true,
    clearLocalStorage: clearLS = true,
    clearSessionStorage: clearSS = true,
    clearBrowserCache: clearBC = true,
    clearServiceWorkers = true,
    clearIndexedDB: clearIDB = true,
    clearSvelteStores = true,
    preserveVersionKey = false,
  } = options;

  console.group('[CacheManager] Full Cleanup');

  // Log state before cleanup
  logCacheState('BEFORE cleanup');

  // Synchronous operations
  if (clearCookies) clearAllCookies();
  if (clearLS) clearLocalStorage(preserveVersionKey);
  if (clearSS) clearSessionStorage();
  if (clearSvelteStores) resetAllStores();

  // Asynchronous operations
  const asyncTasks: Promise<void>[] = [];
  if (clearBC) asyncTasks.push(clearBrowserCache());
  if (clearServiceWorkers) asyncTasks.push(unregisterServiceWorkers());
  if (clearIDB) asyncTasks.push(clearIndexedDB());

  await Promise.all(asyncTasks);

  // Log state after cleanup
  logCacheState('AFTER cleanup');

  console.groupEnd();
}

/**
 * Check for version change and perform cleanup if needed.
 * This should be called on app startup.
 * 
 * @returns true if a version change was detected and cleanup was performed
 */
export async function checkVersionAndCleanup(): Promise<boolean> {
  const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
  const versionChanged = storedVersion !== null && storedVersion !== APP_VERSION;

  console.group('[CacheManager] Version Check');

  if (versionChanged) {
    console.groupEnd();
    
    // Preserve version key so we can update it after cleanup
    await performFullCleanup({
      clearCookies: true,
      clearLocalStorage: true,
      clearSessionStorage: true,
      clearBrowserCache: true,
      clearServiceWorkers: true,
      clearIndexedDB: true,
      clearSvelteStores: true,
      preserveVersionKey: true,  // Keep version key during version change
    });
    updateStoredVersion();
    
    // Force a full page reload to ensure all cached resources are refreshed
    // Add a small delay to ensure cleanup operations complete
    setTimeout(() => {
      window.location.reload();
    }, 100);
    
    return true;
  }

  // Update version if this is first visit (storedVersion is null)
  if (storedVersion === null) {
    updateStoredVersion();
  }
  
  console.groupEnd();
  return false;
}

/**
 * Initialize cache management on login page.
 * This performs cleanup when user reaches the login page (not authenticated).
 * All user state is cleared - preferences will be restored from database after login.
 * 
 * @returns Promise that resolves when initialization is complete
 */
export async function initializeOnLoginPage(): Promise<void> {
  // Check for version change first
  const versionChanged = await checkVersionAndCleanup();
  
  // If version changed, the page will reload, so don't continue
  if (versionChanged) {
    return;
  }

  
  // Perform cleanup for login page (user is logging out or starting fresh)
  // Clear ALL state - user preferences will be restored from database after login
  await performFullCleanup({
    clearCookies: true,
    clearLocalStorage: true,
    clearSessionStorage: true,
    clearBrowserCache: false,  // Don't clear browser cache on every login, only on version change
    clearServiceWorkers: false,
    clearIndexedDB: false,
    clearSvelteStores: true,   // Reset all Svelte stores
    preserveVersionKey: true,  // Keep version tracking
  });
  
}

/**
 * Force clear all caches and reload the page.
 * This can be called manually (e.g., from a "Clear Cache" button in settings).
 */
export async function forceClearAndReload(): Promise<void> {
  await performFullCleanup();
  updateStoredVersion();
  window.location.reload();
}
